import { Dispatch, useCallback } from 'react';
import {
  InterruptionDto,
  InterruptionEmployeeResponseStatus,
  InterruptionsAction,
  InterruptionsActionType,
  SetInterruptionsViewStatusParams,
} from '../../types';

type UseInterruptionsStateActionsParams = Readonly<{
  dispatch: Dispatch<InterruptionsAction>;
}>;

const useInterruptionsStateActions = ({ dispatch }: UseInterruptionsStateActionsParams) => {
  const setInterruptionsRequest = useCallback(() => {
    dispatch({ type: InterruptionsActionType.SetInterruptionsRequest });
  }, [dispatch]);

  const setInterruptionsSuccess = useCallback(
    (items: InterruptionDto[]) => {
      dispatch({
        payload: items,
        type: InterruptionsActionType.SetInterruptionsSuccess,
      });
    },
    [dispatch],
  );

  const setInterruptionsFailure = useCallback(
    (error: string) => {
      dispatch({ payload: error, type: InterruptionsActionType.SetInterruptionsFailure });
    },
    [dispatch],
  );

  const addInterruption = useCallback(
    (item: InterruptionDto) => {
      dispatch({
        payload: item,
        type: InterruptionsActionType.AddInterruption,
      });
    },
    [dispatch],
  );

  const removeInterruption = useCallback(
    (id: string) => {
      dispatch({ payload: id, type: InterruptionsActionType.RemoveInterruption });
    },
    [dispatch],
  );

  const setInterruptionResponseStatus = useCallback(
    (id: string, employeeResponseStatus: InterruptionEmployeeResponseStatus) => {
      dispatch({
        payload: { id, employeeResponseStatus },
        type: InterruptionsActionType.SetInterruptionResponseStatus,
      });
    },
    [dispatch],
  );

  const setInterruptionsViewStatus = useCallback(
    ({ ids, viewStatus }: SetInterruptionsViewStatusParams) => {
      dispatch({ payload: { ids, viewStatus }, type: InterruptionsActionType.SetInterruptionsViewStatus });
    },
    [dispatch],
  );

  const setEmergencyInterruptionsRequest = useCallback(() => {
    dispatch({ type: InterruptionsActionType.SetEmergencyInterruptionsRequest });
  }, [dispatch]);

  const setEmergencyInterruptionsSuccess = useCallback(
    (items: InterruptionDto[]) => {
      dispatch({ payload: items, type: InterruptionsActionType.SetEmergencyInterruptionsSuccess });
    },
    [dispatch],
  );

  const mergeEmergencyInterruptionsSuccess = useCallback(
    (items: InterruptionDto[]) => {
      dispatch({
        payload: items,
        type: InterruptionsActionType.MergeEmergencyInterruptionsSuccess,
      });
    },
    [dispatch],
  );

  const setEmergencyInterruptionsFailure = useCallback(
    (error: string) => {
      dispatch({ payload: error, type: InterruptionsActionType.SetEmergencyInterruptionsFailure });
    },
    [dispatch],
  );

  const removeEmergencyInterruption = useCallback(
    (id: string) => {
      dispatch({ payload: id, type: InterruptionsActionType.RemoveEmergencyInterruption });
    },
    [dispatch],
  );

  const addEmergencyInterruption = useCallback(
    (item: InterruptionDto) => {
      dispatch({ payload: item, type: InterruptionsActionType.AddEmergencyInterruption });
    },
    [dispatch],
  );

  const reset = useCallback(() => {
    dispatch({ type: InterruptionsActionType.Reset });
  }, [dispatch]);

  return {
    addEmergencyInterruption,
    addInterruption,
    mergeEmergencyInterruptionsSuccess,
    removeEmergencyInterruption,
    removeInterruption,
    reset,
    setEmergencyInterruptionsFailure,
    setEmergencyInterruptionsRequest,
    setEmergencyInterruptionsSuccess,
    setInterruptionResponseStatus,
    setInterruptionsFailure,
    setInterruptionsRequest,
    setInterruptionsSuccess,
    setInterruptionsViewStatus,
  };
};

export default useInterruptionsStateActions;
